/**
 * useDueDiligence Hook
 * 
 * Manages due diligence data for an opportunity including
 * checklist items, status updates, and progress tracking.
 */

import { useState, useEffect, useCallback } from 'react';
import { authApi } from '../shared/utils/api';
import { logger } from '../shared/utils/logger';
import {
  DueDiligence,
  DueDiligenceItem,
  UpdateDueDiligenceItemRequest,
  UpdateDueDiligenceRequest,
  BasePropertyType,
  ModuleCode,
  FlagType,
  ItemStatus,
} from '../shared/types/due-diligence';

interface UseDueDiligenceProps {
  portfolioId: number;
  opportunityId: number;
}

interface UseDueDiligenceReturn {
  // Data
  dueDiligence: DueDiligence | null;
  items: DueDiligenceItem[];

  // Loading states
  loading: boolean;
  initializing: boolean;
  updatingItem: number | null; // Item ID being updated

  // Error
  error: string | null;

  // Computed
  completionPercent: number;
  flagCounts: Record<FlagType | 'none', number>;
  statusCounts: Record<ItemStatus, number>;

  // Actions
  refresh: () => Promise<void>;
  initialize: (propertyType: BasePropertyType, modules: ModuleCode[], items: any[]) => Promise<boolean>;
  updateItem: (itemId: number, data: UpdateDueDiligenceItemRequest) => Promise<boolean>;
  updateConfig: (data: Pick<UpdateDueDiligenceRequest, 'currentPhase' | 'finalDecision' | 'decisionRationale'>) => Promise<boolean>;
}

export function useDueDiligence({ portfolioId, opportunityId }: UseDueDiligenceProps): UseDueDiligenceReturn {
  const [dueDiligence, setDueDiligence] = useState<DueDiligence | null>(null);
  const [loading, setLoading] = useState(true);
  const [initializing, setInitializing] = useState(false);
  const [updatingItem, setUpdatingItem] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Fetch due diligence data
  const fetchDueDiligence = useCallback(async () => {
    if (!portfolioId || !opportunityId) return;

    setLoading(true);
    setError(null);

    try {
      const response = await authApi.getDueDiligence(portfolioId, opportunityId);
      
      if (response.dueDiligence) {
        setDueDiligence(response.dueDiligence);
      }
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to load due diligence';
      logger.error('Failed to fetch due diligence', err, 'useDueDiligence');
      setError(message);
    } finally {
      setLoading(false);
    }
  }, [portfolioId, opportunityId]);

  // Initial fetch
  useEffect(() => {
    fetchDueDiligence();
  }, [fetchDueDiligence]);

  // Initialize due diligence with modules and items
  const initialize = useCallback(async (
    propertyType: BasePropertyType,
    modules: ModuleCode[],
    items: any[]
  ): Promise<boolean> => {
    setInitializing(true);
    setError(null);

    try {
      const response = await authApi.initializeDueDiligence(portfolioId, opportunityId, {
        basePropertyType: propertyType,
        enabledModules: modules,
        items,
      });

      if (response.success) {
        await fetchDueDiligence();
        logger.info('Due diligence initialized', 'useDueDiligence', { opportunityId, itemsCreated: response.itemsCreated });
        return true;
      } else {
        setError('Failed to initialize due diligence');
        return false;
      }
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to initialize';
      logger.error('Initialize due diligence failed', err, 'useDueDiligence');
      setError(message);
      return false;
    } finally {
      setInitializing(false);
    }
  }, [portfolioId, opportunityId, fetchDueDiligence]);

  // Update a single item
  const updateItem = useCallback(async (
    itemId: number,
    data: UpdateDueDiligenceItemRequest
  ): Promise<boolean> => {
    setUpdatingItem(itemId);
    setError(null);

    try {
      const response = await authApi.updateDueDiligenceItem(portfolioId, opportunityId, itemId, data);

      if (response.success && response.item) {
        // Update the item in local state
        setDueDiligence(prev => {
          if (!prev || !prev.items) return prev;
          return {
            ...prev,
            items: prev.items.map(item => 
              item.id === itemId ? { ...item, ...response.item } : item
            ),
          };
        });
        return true;
      } else {
        setError('Failed to update item');
        return false;
      }
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to update item';
      logger.error('Update item failed', err, 'useDueDiligence');
      setError(message);
      return false;
    } finally {
      setUpdatingItem(null);
    }
  }, [portfolioId, opportunityId]);

  const updateConfig = useCallback(async (data: Pick<UpdateDueDiligenceRequest, 'currentPhase' | 'finalDecision' | 'decisionRationale'>): Promise<boolean> => {
    setError(null);

    try {
      const response = await authApi.createOrUpdateDueDiligence(portfolioId, opportunityId, data);

      if (response.dueDiligence) {
        setDueDiligence(prev => prev ? { ...prev, ...response.dueDiligence } : response.dueDiligence);
        return true;
      } else {
        setError('Failed to update configuration');
        return false;
      }
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to update';
      logger.error('Update config failed', err, 'useDueDiligence');
      setError(message);
      return false;
    }
  }, [portfolioId, opportunityId]);

  // Computed values
  const items = dueDiligence?.items || [];
  
  const completionPercent = dueDiligence?.completionPercent || 0;
  
  const flagCounts: Record<FlagType | 'none', number> = {
    big_red: 0, red: 0, yellow: 0, green: 0, big_green: 0,
    clear: 0, pending: 0, not_applicable: 0, none: 0,
  };
  items.forEach(item => {
    if (item.flag) flagCounts[item.flag]++;
    else flagCounts.none++;
  });

  const statusCounts: Record<ItemStatus, number> = {
    pending: 0, in_progress: 0, complete: 0, not_applicable: 0,
  };
  items.forEach(item => {
    statusCounts[item.status]++;
  });

  return {
    dueDiligence,
    items,
    loading,
    initializing,
    updatingItem,
    error,
    completionPercent,
    flagCounts,
    statusCounts,
    refresh: fetchDueDiligence,
    initialize,
    updateItem,
    updateConfig,
  };
}

